import { useMemo, useState } from "react";
import {
  useGetAuditReportsQuery,
  useGetAuditProposalsQuery,
  useRunAuditMutation,
  useApplyAuditProposalMutation,
  useRejectAuditProposalMutation,
  useCopyAuditProposalMutation,
  type AuditProposal,
  type AuditReport,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StalenessBadge } from "@/components/domain/StalenessBadge";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import { fmtTime, fmtRelative } from "@/lib/format";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Copy, Play } from "lucide-react";

/** Audit bot — Gemini-driven review of hourly + scalp engines. Each
 *  proposal is either a SETTING change (one-click apply, validated by
 *  the same clamps as /api/settings) or a CODE change (gives you a
 *  ready-to-paste Claude Code prompt). */
export default function Audit() {
  const reportsQ = useGetAuditReportsQuery(undefined, {
    pollingInterval: 30000,
  });
  const [runAudit, { isLoading: running }] = useRunAuditMutation();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const onRun = async (engine: "hourly" | "scalp" | "both") => {
    try {
      const out = await runAudit(engine).unwrap();
      const total = out.reduce((acc, r) => acc + (r.proposals_count ?? 0), 0);
      toast.success(`Audit done — ${total} proposal${total === 1 ? "" : "s"}`);
    } catch (e: any) {
      toast.error(e?.data?.detail ?? "Audit failed");
    }
  };

  const toggle = (rid: number) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(rid)) next.delete(rid); else next.add(rid);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <span>Audit bot</span>
              <StalenessBadge
                ts={(reportsQ.data ?? [])[0]?.created_at}
                warnHours={26} staleHours={48} veryStaleHours={96}
                label="last report"
              />
              <span className="text-xs text-muted ml-2 font-normal">
                Gemini reviews recent trades and proposes setting or code changes.
                Daily at 12:00 UTC + on demand.
              </span>
            </CardTitle>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="ghost"
                    disabled={running}
                    onClick={() => onRun("scalp")}>
              <Play className="w-3.5 h-3.5" /> Audit scalp
            </Button>
            <Button size="sm" variant="ghost"
                    disabled={running}
                    onClick={() => onRun("hourly")}>
              <Play className="w-3.5 h-3.5" /> Audit hourly
            </Button>
            <Button size="sm" variant="primary"
                    disabled={running}
                    onClick={() => onRun("both")}>
              <Play className="w-3.5 h-3.5" /> Run both now
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {reportsQ.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (reportsQ.data ?? []).length === 0 ? (
            <p className="text-sm text-muted">
              No audit reports yet. Click "Run both now" to generate the first one.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {(reportsQ.data ?? []).map((r) => (
                <ReportRow key={r.id} report={r}
                            expanded={expanded.has(r.id)}
                            onToggle={() => toggle(r.id)} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ReportRow({ report, expanded, onToggle }: {
  report: AuditReport; expanded: boolean; onToggle: () => void;
}) {
  return (
    <div className="py-3">
      <div onClick={onToggle}
            className="flex items-start gap-2 cursor-pointer hover:bg-bg2/50 -mx-2 px-2 py-1 rounded">
        <span className="mt-1">
          {expanded
            ? <ChevronDown className="w-3.5 h-3.5" />
            : <ChevronRight className="w-3.5 h-3.5" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge tone={report.engine === "scalp" ? "info" : "default"}>
              {report.engine}
            </Badge>
            <Badge tone={report.trigger === "manual" ? "warn" : "muted"}>
              {report.trigger}
            </Badge>
            {report.status === "error" && <Badge tone="bad">error</Badge>}
            <span className="text-xs text-muted">
              {fmtTime(report.created_at)} · {fmtRelative(report.created_at)}
            </span>
            <span className="text-xs ml-auto">
              {report.pending_count > 0 ? (
                <Badge tone="warn">{report.pending_count} pending</Badge>
              ) : (
                <span className="text-muted">{report.total_count} proposal{report.total_count === 1 ? "" : "s"}</span>
              )}
            </span>
          </div>
          <p className="text-sm mt-1">{report.summary || "(no summary)"}</p>
        </div>
      </div>
      {expanded && <ReportProposals reportId={report.id} />}
    </div>
  );
}

function ReportProposals({ reportId }: { reportId: number }) {
  const q = useGetAuditProposalsQuery(reportId);
  if (q.isLoading) return <Skeleton className="h-16 w-full mt-2" />;
  const proposals = q.data?.proposals ?? [];
  if (proposals.length === 0) {
    return (
      <p className="text-xs text-muted mt-2 ml-6">
        No proposals on this report — engine was in good shape per the LLM.
      </p>
    );
  }
  return (
    <div className="mt-2 ml-6 space-y-3">
      {proposals.map((p) => (
        <ProposalCard key={p.id} proposal={p} />
      ))}
    </div>
  );
}

function ProposalCard({ proposal }: { proposal: AuditProposal }) {
  const [applyMut, { isLoading: applying }] = useApplyAuditProposalMutation();
  const [rejectMut, { isLoading: rejecting }] = useRejectAuditProposalMutation();
  const [copyMut, { isLoading: copying }] = useCopyAuditProposalMutation();

  const patch = useMemo(() => {
    if (!proposal.patch_json) return null;
    try { return JSON.parse(proposal.patch_json); } catch { return null; }
  }, [proposal.patch_json]);

  const isPending = proposal.status === "pending";

  const onApply = async () => {
    if (!confirm(`Apply this setting change?\n\n${proposal.title}`)) return;
    try {
      const r = await applyMut(proposal.id).unwrap();
      toast.success(`Applied ${Object.keys((r as any).applied ?? {}).length} key(s)`);
    } catch (e: any) {
      toast.error(e?.data?.detail ?? "Apply failed");
    }
  };

  const onReject = async () => {
    try {
      await rejectMut(proposal.id).unwrap();
      toast.success("Rejected");
    } catch (e: any) {
      toast.error(e?.data?.detail ?? "Reject failed");
    }
  };

  const onCopy = async () => {
    try {
      const r = await copyMut(proposal.id).unwrap();
      const text = r.code_prompt || proposal.code_prompt || "";
      if (text && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        toast.success("Claude prompt copied to clipboard");
      } else {
        toast.warning("Marked copied (clipboard unavailable — copy manually)");
      }
    } catch (e: any) {
      toast.error(e?.data?.detail ?? "Copy failed");
    }
  };

  const tone =
    proposal.status === "applied" ? "good"
    : proposal.status === "rejected" ? "muted"
    : proposal.status === "copied" ? "info"
    : "warn";

  return (
    <div className="border border-border rounded-md p-3 bg-bg2/30">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <Badge tone={proposal.kind === "setting" ? "info" : "warn"}>
          {proposal.kind}
        </Badge>
        <Badge tone={tone}>{proposal.status}</Badge>
        <span className="text-sm font-medium">{proposal.title}</span>
      </div>
      <p className="text-xs text-muted whitespace-pre-wrap mb-2">
        {proposal.rationale}
      </p>

      {proposal.kind === "setting" && patch && (
        <pre className="text-xs bg-bg2 border border-border rounded p-2 overflow-auto mb-2">
{JSON.stringify(patch, null, 2)}
        </pre>
      )}

      {proposal.kind === "code" && (
        <div className="space-y-1 mb-2">
          {proposal.file_hint && (
            <div className="text-xs">
              <span className="text-muted">file hint:</span>{" "}
              <span className="font-mono">{proposal.file_hint}</span>
            </div>
          )}
          {proposal.code_prompt && (
            <details className="text-xs">
              <summary className="cursor-pointer text-accent">Claude prompt (click to view)</summary>
              <pre className="bg-bg2 border border-border rounded p-2 mt-1 whitespace-pre-wrap break-words">
                {proposal.code_prompt}
              </pre>
            </details>
          )}
        </div>
      )}

      {isPending && (
        <div className="flex gap-2 mt-2">
          {proposal.kind === "setting" && (
            <Button size="sm" variant="primary" disabled={applying} onClick={onApply}>
              Apply
            </Button>
          )}
          {proposal.kind === "code" && (
            <Button size="sm" variant="primary" disabled={copying} onClick={onCopy}>
              <Copy className="w-3.5 h-3.5" /> Copy Claude prompt
            </Button>
          )}
          <Button size="sm" variant="ghost" disabled={rejecting} onClick={onReject}>
            Reject
          </Button>
        </div>
      )}

      {!isPending && proposal.decided_at && (
        <div className="text-xs text-muted mt-1">
          {proposal.status} · {fmtRelative(proposal.decided_at)}
          {proposal.decided_by && ` · by ${proposal.decided_by}`}
        </div>
      )}

      {proposal.note && proposal.status === "applied" && (
        <Alert tone="info" className="mt-2">
          <pre className="text-xs whitespace-pre-wrap">{proposal.note}</pre>
        </Alert>
      )}
    </div>
  );
}
